import { Fragment, useEffect, useMemo, useState } from 'react';
import {
  BarList, Card, Select, SelectItem, Table, TableBody, TableCell, TableHead,
  TableHeaderCell, TableRow, Text, Title,
} from '@tremor/react';
import type { ServiceProductivity, ServiceProductivityTech } from '../../api/service';
import { fetchServiceProductivity } from '../../api/service';
import DateRangePresets from '../../components/DateRangePresets';
import EmptyState from '../../components/EmptyState';
import ErrorRetry from '../../components/ErrorRetry';
import InfoTooltip from '../../components/InfoTooltip';
import TableSkeleton from '../../components/TableSkeleton';
import { formatMxn, localYMD } from '../../lib/format';
import { t } from '../../lib/i18n';

type LeaderboardMetric = 'revenue_mxn' | 'efficiency' | 'eff_rate_mxn' | 'jobs';

const fmtHrs = new Intl.NumberFormat('es-MX', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

function dateNDaysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return localYMD(d);
}

function pct(x: number): string {
  return `${Math.round(x * 100)}%`;
}

/**
 * Pure, render-free helpers (vitest-tested directly).
 */

/** Techs ranked by the selected metric, descending — feeds the leaderboard BarList. */
export function leaderboardData(
  techs: ServiceProductivityTech[],
  metric: LeaderboardMetric,
): { name: string; value: number }[] {
  return techs
    .map((r) => ({ name: r.tech_name || r.tech, value: r[metric] }))
    .sort((a, b) => b.value - a.value);
}

/** Aggregate revenue by category across every technician — feeds the category-mix chart. */
export function categoryMix(techs: ServiceProductivityTech[]): { name: string; value: number }[] {
  const m = new Map<string, number>();
  for (const tech of techs) {
    for (const cat of tech.categories) {
      m.set(cat.category, (m.get(cat.category) ?? 0) + cat.revenue_mxn);
    }
  }
  return [...m.entries()].map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
}

/** Per-tech table rows, sorted by revenue descending (same convention as the sibling Técnicos tab). */
export function sortedTechs(techs: ServiceProductivityTech[]): ServiceProductivityTech[] {
  return [...techs].sort((a, b) => b.revenue_mxn - a.revenue_mxn);
}

/**
 * Rendimiento view — per-technician productivity (jobs, estimated vs. actual
 * hours, efficiency, effective $/hr, utilización) joined to labor GP (when
 * the caller has cost:visible) and a heuristic comeback rate. Unlike the
 * other cockpit tabs, this one owns its own from/to window (default trailing
 * 30 days) — it fetches its own report from /api/service-productivity rather
 * than reading the cockpit's shared /api/service-orders result. The store
 * filter is inherited from the cockpit header (the `sid` prop), matching how
 * the sibling Técnicos tab is wired.
 */
export default function Rendimiento({ sid }: { sid: number | null }) {
  const [from, setFrom] = useState(dateNDaysAgo(30));
  const [to, setTo] = useState(dateNDaysAgo(0));
  const [metric, setMetric] = useState<LeaderboardMetric>('revenue_mxn');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [data, setData] = useState<ServiceProductivity | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = () => {
    setErr(null);
    setData(null);
    fetchServiceProductivity({ from, to, sid })
      .then(setData)
      .catch((e: Error) => setErr(e.message ?? String(e)));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [from, to, sid]);

  const c = t.service.rendimiento;
  // The fetcher throws "404: módulo de taller no habilitado" when service_shop
  // is off (see fetchServiceProductivity) — treat that sentinel as an
  // access-denied state (same disabled-module copy the cockpit shell shows)
  // rather than a retryable error.
  const notEnabled = err != null && err.startsWith('404');

  const toggle = (key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const board = useMemo(() => (data ? leaderboardData(data.techs, metric) : []), [data, metric]);
  const mix = useMemo(() => (data ? categoryMix(data.techs) : []), [data]);
  const rows = useMemo(() => (data ? sortedTechs(data.techs) : []), [data]);
  const showCost = data?.totals.labor_cost_mxn !== undefined;
  const colCount = 9 + (showCost ? 2 : 0) + 1; // metric cols + trailing chevron col

  const boardValueFormatter = (n: number) => {
    if (metric === 'revenue_mxn' || metric === 'eff_rate_mxn') return formatMxn(n);
    if (metric === 'efficiency') return pct(n);
    return String(n);
  };

  return (
    <div className="space-y-4">
      <Card className="flex flex-col md:flex-row md:items-center gap-3">
        <DateRangePresets from={from} to={to} onChange={({ from: f, to: t2 }) => { setFrom(f); setTo(t2); }} />
      </Card>

      {notEnabled ? (
        <EmptyState message={t.service.disabled} />
      ) : err ? (
        <ErrorRetry message={err} onRetry={load} />
      ) : !data ? (
        <TableSkeleton rows={6} cols={8} />
      ) : data.techs.length === 0 ? (
        <EmptyState message={c.empty} />
      ) : (
        <>
          <Card className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Kpi label={c.cols.jobs} value={String(data.totals.jobs)} />
            <Kpi label={c.kpi.efficiency} value={data.totals.act_hrs > 0 ? pct(data.totals.efficiency) : '—'} />
            <Kpi label={c.kpi.revenue} value={formatMxn(data.totals.revenue_mxn)} />
            <Kpi label={c.kpi.comebackRate} value={pct(data.totals.comeback_rate)} />
            {data.totals.labor_gp_mxn !== undefined && (
              <Kpi label={c.kpi.laborGp} value={formatMxn(data.totals.labor_gp_mxn)} />
            )}
          </Card>

          <Card>
            <div className="flex items-center justify-between gap-3">
              <Title className="text-base">{c.leaderboardTitle}</Title>
              <Select
                value={metric}
                onValueChange={(v) => setMetric(v as LeaderboardMetric)}
                className="w-56"
                enableClear={false}
                aria-label={c.leaderboardMetric}
              >
                <SelectItem value="revenue_mxn">{c.cols.revenue}</SelectItem>
                <SelectItem value="efficiency">{c.cols.efficiency}</SelectItem>
                <SelectItem value="eff_rate_mxn">{c.cols.effRate}</SelectItem>
                <SelectItem value="jobs">{c.cols.jobs}</SelectItem>
              </Select>
            </div>
            <BarList className="mt-3" data={board} valueFormatter={boardValueFormatter} />
          </Card>

          <Card>
            <Title className="text-base">{c.categoryMixTitle}</Title>
            <BarList className="mt-3" data={mix} valueFormatter={(n: number) => formatMxn(n)} />
          </Card>

          <Card className="p-0 overflow-hidden">
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>{c.cols.tech}</TableHeaderCell>
                  <TableHeaderCell className="text-right">{c.cols.jobs}</TableHeaderCell>
                  <TableHeaderCell className="text-right">{c.cols.estHrs}</TableHeaderCell>
                  <TableHeaderCell className="text-right">{c.cols.actHrs}</TableHeaderCell>
                  <TableHeaderCell className="text-right">{c.cols.efficiency}</TableHeaderCell>
                  <TableHeaderCell className="text-right">{c.cols.effRate}</TableHeaderCell>
                  <TableHeaderCell className="text-right">{c.cols.utilization}</TableHeaderCell>
                  <TableHeaderCell className="text-right">
                    <InfoTooltip text={c.comebackDisclaimer.replace('{days}', String(data.comeback_days))}>
                      {c.cols.comebacks}
                    </InfoTooltip>
                  </TableHeaderCell>
                  <TableHeaderCell className="text-right">{c.cols.revenue}</TableHeaderCell>
                  {showCost && (
                    <>
                      <TableHeaderCell className="text-right">{c.cols.laborCost}</TableHeaderCell>
                      <TableHeaderCell className="text-right">{c.cols.laborGp}</TableHeaderCell>
                    </>
                  )}
                  <TableHeaderCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => {
                  const key = `${r.sid}-${r.tech}`;
                  const rowShowsCost = r.labor_cost_mxn !== undefined;
                  const isOpen = expanded.has(key);
                  return (
                    <Fragment key={key}>
                      <TableRow className="cursor-pointer hover:bg-tremor-background-muted" onClick={() => toggle(key)}>
                        <TableCell>{r.tech_name || r.tech}</TableCell>
                        <TableCell className="text-right tabular-nums">{r.jobs}</TableCell>
                        <TableCell className="text-right tabular-nums">{fmtHrs.format(r.est_hrs)}</TableCell>
                        <TableCell className="text-right tabular-nums">{fmtHrs.format(r.act_hrs)}</TableCell>
                        <TableCell className="text-right tabular-nums">{r.act_hrs > 0 ? pct(r.efficiency) : '—'}</TableCell>
                        <TableCell className="text-right tabular-nums">{formatMxn(r.eff_rate_mxn)}</TableCell>
                        <TableCell className="text-right tabular-nums">{fmtHrs.format(r.util_hrs_per_day)}</TableCell>
                        <TableCell className="text-right tabular-nums">{r.comebacks} ({pct(r.comeback_rate)})</TableCell>
                        <TableCell className="text-right tabular-nums">{formatMxn(r.revenue_mxn)}</TableCell>
                        {showCost && (
                          <>
                            <TableCell className="text-right tabular-nums">
                              {rowShowsCost ? formatMxn(r.labor_cost_mxn!) : '—'}
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                              {rowShowsCost ? `${formatMxn(r.labor_gp_mxn!)} (${pct(r.labor_gp_pct ?? 0)})` : '—'}
                            </TableCell>
                          </>
                        )}
                        <TableCell className="text-right text-tremor-content text-xs">{isOpen ? '▾' : '▸'}</TableCell>
                      </TableRow>
                      {isOpen && (
                        <TableRow className="bg-tremor-background-subtle text-xs">
                          <TableCell colSpan={colCount} className="pl-8">
                            {r.categories.length === 0 ? (
                              <Text className="text-tremor-content">{c.empty}</Text>
                            ) : (
                              <div className="flex flex-wrap gap-x-6 gap-y-1">
                                {r.categories.map((cat) => (
                                  <span key={cat.category || '—'}>
                                    <span className="font-medium">{cat.category || '—'}</span>
                                    {': '}
                                    {formatMxn(cat.revenue_mxn)} · {fmtHrs.format(cat.act_hrs)}h · {cat.jobs} {t.service.jobsLabel}
                                  </span>
                                ))}
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        </>
      )}
    </div>
  );
}

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-tremor-content text-xs">{label}</div>
      <div className="text-tremor-content-strong text-lg font-semibold tabular-nums">{value}</div>
    </div>
  );
}
